import { useCallback, useContext, useEffect } from 'react'
import type { MouseEvent } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import { listBinders, type Binder, type BindersList } from '../api/collectionBinders'
import { BINDER_KIND } from '../api/collectionBinders.codes'
import { getCollectionSnapshot } from './useCollection'
import { bindersQueryKey } from './useBinders'
import { useSupabaseToken } from './useSupabaseToken'
import { buildScannerIntent, newScanNonce, SCANNER_INTENT_URL, type ScanTargetBinder, type ScanTargetDeck, type ScanTargetKind } from '../lib/androidApp'
import { collectionOwnerOf } from '../lib/collectionOwner'
import { deckTitle } from '../lib/deckTitle'
import { fetchDeckNameIndex, type DeckNameRow } from '../lib/deckNameIndex'
import { recordScanLaunch, scanOwnerKey } from '../lib/scanLaunch'

// The scan links' click handler inside the Android app (Option 2, plan
// section 1): at click time it builds the scanner intent with this owner's
// manual binders and saved decks (most recently updated first), writes the
// launch record its nonce points at, and puts the URL on the link the user is
// following (or navigates to it, for a menu item). The lists are read ahead,
// on mount, into a small module cache, so the click itself stays synchronous
// and keeps its user gesture. Whatever has not loaded yet is simply not
// offered: the scanner still opens, with fewer targets.

/** A target the link preselects ("Scan into this deck / binder"). */
export interface ScannerPreTarget {
  kind: ScanTargetKind
  id: string
  name: string
  cmd?: string
}

const TARGETS_TTL_MS = 60_000
const decksCache = new Map<string, { at: number; rows: DeckNameRow[] }>()
const bindersCache = new Map<string, { at: number; binders: Binder[] }>()
const inFlight = new Set<string>()
/** A failed read waits this long before the next try (the effect runs every render). */
const FAILURE_COOLDOWN_MS = 30_000
const failedAt = new Map<string, number>()

/** Test seam. */
export function __resetScannerTargets(): void {
  decksCache.clear()
  bindersCache.clear()
  inFlight.clear()
  failedAt.clear()
}

function fresh(at: number | undefined): boolean {
  return at !== undefined && Date.now() - at < TARGETS_TTL_MS
}

function coolingDown(key: string): boolean {
  const at = failedAt.get(key)
  return at !== undefined && Date.now() - at < FAILURE_COOLDOWN_MS
}

function binderKey(userId: string, collectionId: string): string {
  // eslint-disable-next-line i18next/no-literal-string -- a cache key
  return `${userId}\u0000${collectionId}`
}

function prefetch(owner: string | null, ownerKey: string, collectionId: string | null): void {
  // eslint-disable-next-line i18next/no-literal-string -- a cache key prefix
  const dk = `d:${ownerKey}`
  if (!fresh(decksCache.get(ownerKey)?.at) && !inFlight.has(dk) && !coolingDown(dk)) {
    inFlight.add(dk)
    fetchDeckNameIndex(owner)
      .then(rows => { decksCache.set(ownerKey, { at: Date.now(), rows }); failedAt.delete(dk) })
      .catch(() => { failedAt.set(dk, Date.now()) })
      .finally(() => { inFlight.delete(dk) })
  }
  // Binders whenever there is an owner and a collection. The per-copy
  // capability is NOT required here: only the Collection page probes it, so
  // a launch from a deck page after a reload never fetched binders and the
  // scanner's Binder option came up empty. A collection without binders
  // answers an error or an empty list, which leaves the cache empty.
  if (!owner || !collectionId) return
  const key = binderKey(owner, collectionId)
  // eslint-disable-next-line i18next/no-literal-string -- a cache key prefix
  const bk = `b:${key}`
  if (fresh(bindersCache.get(key)?.at) || inFlight.has(bk) || coolingDown(bk)) return
  inFlight.add(bk)
  listBinders({ userId: owner, collectionId })
    .then(list => { bindersCache.set(key, { at: Date.now(), binders: list.binders }); failedAt.delete(bk) })
    .catch(() => { failedAt.set(bk, Date.now()) })
    .finally(() => { inFlight.delete(bk) })
}

export function useScannerLaunch(enabled: boolean, pre?: ScannerPreTarget | null): {
  href: string
  /** For an <a href={href}>: rewrites the href the click is about to follow. */
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void
  /** For a button or menu item: navigates to the scanner. */
  launch: () => void
} {
  const identity = useSupabaseToken()
  const owner = collectionOwnerOf(identity)
  const ownerKey = scanOwnerKey(owner)
  const queryClient = useContext(QueryClientContext)

  // Every render while enabled (prefetch is a no-op while fresh or in
  // flight), so a collection pointer that arrives after mount is still read.
  useEffect(() => {
    if (!enabled || owner === undefined || ownerKey === null) return
    prefetch(owner, ownerKey, getCollectionSnapshot().collectionId)
  })

  const build = useCallback((): string => {
    const collectionId = getCollectionSnapshot().collectionId
    let binders: ScanTargetBinder[] = []
    // A listed binder proves the collection has binders (a non-per-copy
    // collection never lists any), so no separate capability check here.
    if (owner && collectionId) {
      const live = queryClient?.getQueryData<BindersList>(bindersQueryKey(collectionId, owner))?.binders
      const list = live ?? bindersCache.get(binderKey(owner, collectionId))?.binders ?? []
      binders = list
        .filter(b => b.kind === BINDER_KIND.manual && b.binder_id !== null)
        .map(b => ({ id: String(b.binder_id), name: b.name }))
    }
    const rows = ownerKey ? decksCache.get(ownerKey)?.rows ?? [] : []
    const decks: ScanTargetDeck[] = rows.map(r => ({ id: r.id, name: deckTitle(r), cmd: r.commander ?? '' }))
    const nonce = newScanNonce()
    const preTarget = pre
      // eslint-disable-next-line i18next/no-literal-string -- a target kind
      ? pre.kind === 'deck' ? { id: pre.id, name: pre.name, cmd: pre.cmd ?? '' } : { id: pre.id, name: pre.name }
      : null
    const { url, targets } = buildScannerIntent({
      binders, decks, nonce, pre: pre ? { kind: pre.kind, id: pre.id } : null, preTarget,
    })
    if (ownerKey) {
      recordScanLaunch({
        nonce,
        ownerKey,
        binderIds: targets.binders.map(b => b.id),
        deckIds: targets.decks.map(d => d.id),
        collectionId,
        createdAt: Date.now(),
      })
    }
    return url
  }, [owner, ownerKey, queryClient, pre])

  const onClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.href = build()
  }, [build])
  const launch = useCallback(() => {
    window.location.assign(build())
  }, [build])
  return { href: SCANNER_INTENT_URL, onClick, launch }
}
